import { Modal } from '@onbeam/ui';
import { CookieConsentProps } from './CookieConsent.types';
import { useCookieConsentStore } from '@onbeam/utils';
import { CookieConsentModal } from './CookieConsentModal';
import { defaultProps } from './CookieConsent.utils';
import { CookieConsentPopup } from './CookieConsentPopup';

/**
 * A component that allows users to manage their cookie consent preferences.
 *
 * @param {CookieConsentProps} props - The properties for the CookieConsent component.
 * @returns {JSX.Element} The rendered CookieConsent component.
 *
 * @remarks
 * Use in conjunction with the useCookieConsentStore hook from `@onbeam/utils` to manage the popup and modal state and consent preferences.
 *
 * @example
 * In your app layout component, render the CookieConsent component with the desired i18n text and consent domain.
 * ```tsx
 * <CookieConsent
 *  consentDomain=".onbeam.com"
 *  i18n={{
 *  title: 'Cookie Consent',
 *  description: 'This website uses cookies to ensure you get the best experience on our website.',
 * />
 */
export const CookieConsent = ({
  consentDomain,
  ...props
}: CookieConsentProps) => {
  const i18n = {
    ...defaultProps.i18n,
    ...props.i18n,
  };

  const {
    isConsentModalOpen: isOpen,
    openConsentModal: onOpen,
    closeConsentModal: onClose,
  } = useCookieConsentStore();

  return (
    <>
      <Modal
        open={isOpen}
        title={i18n.title}
        description={i18n.modalDescription}
        onOpenChange={(open) => {
          if (open) return onOpen();
          onClose();
        }}
        data-testid="cookie-consent-modal"
      >
        <CookieConsentModal consentDomain={consentDomain} i18n={i18n} />
      </Modal>
      <CookieConsentPopup consentDomain={consentDomain} i18n={i18n} />
    </>
  );
};
